import { useCallback, useContext, useEffect, useState } from "react";
import {
  EXTEND_SUBSCRIPTION_DURATIONS,
  GetPaymentLinkRequest,
  PaymentType,
  SubscriptionPaymentMethodType,
} from "@shared/types";
import { PaymentOption } from "@shared/features/settings/subscription/types";
import { PAYMENT_OPTION_TO_DISPLAY_NAME } from "@shared/features/settings/subscription/constants";
import { useUser } from "@shared/utils/hooks/misc";
import { BuySubscriptionContext } from "features/settings/Subscription/BuySubscription.context";
import useQuery from "utils/hooks/useQuery";
import {
  EMAIL_QUERY_PARAM,
  IS_APPLE_PAY,
  IS_GOOGLE_PAY,
} from "features/settings/common/constants";
import { usePromoCodePrices } from "features/settings/Subscription/usePromoCodePrices.ts";

const useStepWithSummary = () => {
  const {
    subscriptionParams,
    plan,
    isUpgrade,
    subGroupId,
    numApiSlots,
    paymentMethod,
    isCryptoPayment,
    promoCode,
    setPromoCode,
    setPromoCodePrices,
    queryPromoCode,
  } = useContext(BuySubscriptionContext)!;

  const query = useQuery();
  const { user } = useUser();
  const queryEmail = query.get(EMAIL_QUERY_PARAM) || undefined;

  const [email, setEmail] = useState<string | undefined>(queryEmail);
  const userEmail = user?.email || email;

  const { handleGetNewPaymentPrice, getNewPaymentPriceStatus } =
    usePromoCodePrices();

  const {
    step,
    duration: queryDuration,
    paymentMethod: queryPaymentMethod,
  } = subscriptionParams;

  const showDurationSelection = !isUpgrade || IS_APPLE_PAY || IS_GOOGLE_PAY;
  const duration = showDurationSelection
    ? queryDuration || 360
    : plan?.upgradePlan?.subscription_days;

  const getSubscriptionPlanID = (duration: number) => {
    return plan?.[duration]?.subscription_plan_id || 0;
  };

  const subscriptionPlanID = getSubscriptionPlanID(queryDuration);

  const getBaseRequest = useCallback(
    (
      usePromoCode?: boolean,
      defaultPaymentOption?: string,
      duration?: number
    ) => {
      const cryptoPaymentOption =
        defaultPaymentOption || paymentMethod?.payment_option;
      if (isCryptoPayment && (!cryptoPaymentOption || !userEmail)) return;

      const paymentOption = isCryptoPayment
        ? cryptoPaymentOption!
        : PAYMENT_OPTION_TO_DISPLAY_NAME[SubscriptionPaymentMethodType.PAYPAL];
      let baseRequest: GetPaymentLinkRequest | undefined = undefined;
      if (isUpgrade && subscriptionParams.plan) {
        baseRequest = {
          payment_option: paymentOption,
          email: userEmail!,
          payment_type: PaymentType.UPGRADE,
          plan_type: subscriptionParams.plan,
          promo_code: usePromoCode ? promoCode : undefined,
          number_of_clients: numApiSlots || undefined,
        };
      } else if (!isUpgrade && queryDuration) {
        if (subscriptionPlanID) {
          baseRequest = {
            payment_option: paymentOption,
            email: userEmail!,
            payment_type: PaymentType.EXTENSION,
            subscription_plan_id: duration
              ? getSubscriptionPlanID(duration)
              : subscriptionPlanID,
            promo_code: usePromoCode ? promoCode : undefined,
            sub_group_id: subGroupId,
            number_of_clients: numApiSlots || undefined,
          };
        }
      }
      return baseRequest;
    },
    [
      isCryptoPayment,
      paymentMethod,
      subscriptionParams.plan,
      subGroupId,
      duration,
      userEmail,
      subscriptionPlanID,
      promoCode,
      numApiSlots,
    ]
  );

  const onApplyButtonClick = () => {
    if (isUpgrade) {
      const baseRequest = getBaseRequest(true, PaymentOption.PAYPAL);
      if (baseRequest && plan?.upgradePlan)
        handleGetNewPaymentPrice(
          baseRequest,
          plan.upgradePlan.subscription_days
        );
    } else {
      EXTEND_SUBSCRIPTION_DURATIONS.map((duration) => {
        const baseRequest = getBaseRequest(
          true,
          PaymentOption.PAYPAL,
          duration
        );
        if (baseRequest) handleGetNewPaymentPrice(baseRequest, duration);
      });
    }
  };

  useEffect(() => {
    if (queryPromoCode?.length) {
      onApplyButtonClick();
    }
  }, [queryPromoCode]);

  const onRemovePromoClick = () => {
    setPromoCodePrices(undefined);
    setPromoCode(undefined);
  };

  return {
    step,
    duration,
    onApplyButtonClick,
    queryDuration,
    getBaseRequest,
    queryPaymentMethod,
    getNewPaymentPriceStatus,
    setEmail,
    email,
    onRemovePromoClick,
  };
};

export default useStepWithSummary;
